<template>
	<div class="ui-selection ui-selection-action">
		<div
			ref="select"
			v-click-outside="clickOutside"
			:class="selectClasses"
			class="ui-selection-action__wrapper w-100"
		>
			<button
				ref="button"
				class="ui-selection-action__button ui-button--secondary ui-button--block"
				:class="{ 'disabled': disabled }"
				type="button"
				:aria-controls="id"
				:aria-expanded="isShowList ? 'true' : 'false'"
				role="combobox"
				:aria-labelledby="labelId"
				@click="toggleDropDown"
				@focus="isFocused = true"
				@blur="isFocused = false"
				@keydown.enter.exact.prevent="toggleDropDown(true)"
				@keydown.space.exact.prevent="toggleDropDown(true)"
				@keydown.down.exact.prevent="
					() => {
						if (isShowList) {
							setFocus(0);
						} else {
							toggleDropDown(true);
						}
					}
				"
				@keydown.up.exact.prevent="toggleDropDown(true)"
			>
				<UiSmartIcon
					:name="titleIconBefore"
					:size="15"
					class="ui-button__icon ui-button__icon--prepend"
				/>

				<div class="ui-selection-action__button--title">
					{{ title }}
				</div>

				<UiSmartIcon
					:name="titleIconAfter"
					:size="15"
					class="ui-button__icon ui-button__icon--prepend"
				/>
			</button>
			<transition name="slide">
				<div 
					ref="floating"
					class="ui-select-new__dropdown custom-scrollbar custom-scrollbar--small"
					:class="{
						'ui-select-new__dropdown--open': isShowList,
					}"
					:style="floatingStyle"
				>
					<ul class="ui-select-new__dropmenu" tabindex="-1">
						<li v-for="(option, index) in options"
							:key="`ui-select-${index}`"
							ref="options"
							role="option"
							class="ui-select-new__dropmenu-item"
							aria-selected="false"
							tabindex="0"
							@click="onSelect(option)"
							@keydown.enter.exact.prevent="onSelect(option)"
							@keydown.space.exact.prevent="onSelect(option)"
							@keydown.down.exact.prevent="setFocus(index + 1)"
							@keydown.up.exact.prevent="setFocus(index - 1)"
							@keydown.up.alt.prevent="onSelect(option), setFocus(-1)"
							@keydown.esc.exact.prevent="toggleDropDown(false), setFocus(-1)"
							@keydown.tab.exact="closeIfLast(index)"
						>
							<UiSmartIcon
								v-if="option.prependIcon"
								:name="option.prependIcon"
								:size="18"
								color="#2B2B2B"
								class="ui-button__icon ui-button__icon--prepend"
							/>
							{{ option.label }}
						</li>
					</ul>
				</div>
			</transition>
		</div>
	</div>
</template>

<script>
import { randomKey } from '@/utils/common';
import { useFloating, autoUpdate, hide, flip } from '@floating-ui/vue';
import { ref, computed, inject } from '@vue/composition-api';


export default {
	name: 'UiSelectAction',

	props: {
		/**
		 * Данный над которыми происходит действие
		 */
		data: {
			type: Object,
			default: () => ({}),
		},
		/**
		 * Варианты селекта.
		 *
		 * Структура варианта:
		 * - value: value варианта
		 * - action: действие при выборе
		 */
		options: {
			type: Array,
			default: () => [],
		},
		/**
		 * Флаг, переводящий селект в неактивное состяние (делает селект некликабельным).
		 *
		 * @values true | false
		 */
		disabled: {
			type: Boolean,
			default: false,
		},
		title: {
			type: String,
			default: 'Действия',
		},
		titleIconBefore: {
			type: String,
			default: 'burger',
		},
		titleIconAfter: {
			type: String,
			default: 'drop-down',
		},
	},

	setup() {
		const select = ref(null);
		const floating = ref(null);

		const scope = inject('scope', 'default');

		const defaultLayoutPadding = { top: 148, bottom: 18, left: 48, right: 48 };
		const dialogPadding = { top: 52, bottom: 52, left: 48, right: 48 };

		const padding = scope === 'dialog' ? dialogPadding : defaultLayoutPadding;

		const { x, y, strategy, middlewareData } = useFloating(select, floating, {
			strategy: 'fixed',
			placement: 'bottom-start',
			whileElementsMounted: autoUpdate,
			middleware: [
				flip({ crossAxis: true, mainAxis: true, padding }),
				hide({ strategy: 'referenceHidden', padding }),
				hide({ strategy: 'escaped' }),
			],
		});

		const hidefloating = computed(
			() => middlewareData.value.hide?.referenceHidden || middlewareData.value.hide?.escaped
		);

		const floatingStyle = computed(() => {
			const width = select.value?.offsetWidth || 'auto';
			return {
				position: strategy.value,
				top: `${y.value ?? 0}px`,
				left: `${x.value ?? 0}px`,
				width: `${width}px`,
			};
		});

		return { select, floating, x, y, strategy, hidefloating, floatingStyle };
	},

	data() {
		const id = randomKey('ui-select-');
		const labelId = `${id}-label`;

		return {
			manualShowList: false,
			isFocused: false,
			id,
			labelId,
		};
	},

	computed: {
		isShowList() {
			return this.manualShowList;
		},
		selectClasses() {
			return {
				'ui-select-new--disabled': this.disabled,
			};
		},
	},

	methods: {
		toggleDropDown(open) {
			if (!this.disabled) {
				this.manualShowList = typeof open === 'boolean' ? open : !this.isShowList;
				this.$emit(this.isShowList ? 'focus' : 'blur');
			}
		},
		clickOutside() {
			this.manualShowList = false;
			this.$emit('blur');
		},
		onSelect(option) {
			if (option) {
				this.toggleDropDown();
				this.$emit('select', option.value);
				option.action(this.data);
			}
			this.$refs.button.focus();
		},

		setFocus(nextIndex) {
			if (nextIndex < 0) {
				nextIndex = 0;
				this.$refs.button.focus();
				return;
			}

			if (nextIndex >= this.options.length) {
				nextIndex = this.options.length - 1;
			}
			this.$refs.options[nextIndex].focus();
		},
		closeIfLast(index) {
			if (index === this.options.length - 1) {
				this.toggleDropDown(false);
			}
		}
	},
};
</script>

